import { useQuery } from '@tanstack/react-query';
import type { InternshipProcessWithNames } from '../../Types/interfaces';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { GetWorkLogEntries } from '../../helper/apiHelper';
import { useMemo } from 'react';

export const useReadyToSignProcessesOfTutor = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useMemo(() => {
    return processes.filter((process) =>
      process.processSteps.some(
        (step) =>
          step.stepName === 'SigningInternshipConventionByTutor' &&
          step.stepStatus === 1
      )
    );
  }, [processes]);
};

export const useOngoingProcessesOfTutor = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useMemo(() => {
    return processes.filter((process) =>
      process.processSteps.some(
        (step) => step.stepName === 'WorkReportFilling' && step.stepStatus !== 3
      )
    );
  }, [processes]);
};

export const useWaitingForReviewProcesses = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useMemo(() => {
    return processes.filter(
      (process) =>
        process.processSteps.some(
          (step) =>
            step.stepName === 'WorkReportFilling' && step.stepStatus === 3
        ) &&
        process.processSteps.some(
          (step) =>
            step.stepName === 'ValidateWorkReportByTutor' &&
            step.stepStatus === 1
        )
    );
  }, [processes]);
};

export const useReadyToSignWorkReportsOfTutor = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useMemo(() => {
    return processes.filter((process) =>
      process.processSteps.some(
        (step) =>
          step.stepName === 'SigningWorkReportByTutor' &&
          (step.stepStatus === 1 || step.stepStatus === 2)
      )
    );
  }, [processes]);
};

export const useAcceptedProcesses = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useMemo(() => {
    return processes.filter(
      (process) =>
        process.processSteps.some(
          (step) =>
            step.stepName === 'ValidateWorkReportByTutor' &&
            step.stepStatus === 3
        ) &&
        process.processSteps.some(
          (step) =>
            step.stepName === 'CompletingEvaluationReportByOrganization' &&
            step.stepStatus === 1
        )
    );
  }, [processes]);
};

export const useEvaluatedProcesses = (
  processes: Array<InternshipProcessWithNames>
) => {
  return useQuery<InternshipProcessWithNames[]>({
    queryKey: [processes, ...QUERY_KEYS.EVALUATED_PROCESSES_OF_TUTOR],
    queryFn: async () => {
      const filteredProcesses = processes.filter((process) =>
        process.processSteps.some(
          (step) =>
            step.stepName === 'CompletingEvaluationReportByOrganization' &&
            step.stepStatus === 3
        )
      );

      const workLogPromises = filteredProcesses.map(async (process) => {
        return await GetWorkLogEntries(process.workLogId);
      });

      const workLogStatuses = (await Promise.all(workLogPromises)).map(
        (log) => {
          return log[0].workLogStatus;
        }
      );

      return filteredProcesses.filter((_process, index) => {
        return workLogStatuses[index] !== 1;
      });
    },
    enabled: processes && processes.length > 0,
  });
};
